// updatemap.ts ↔ game/updatemap.cpp — the per-tile invalidation engine.
//
// Tracks which playfield tiles need redrawing (m_afInvalid), accumulates damage
// while drawing (m_afInvalidDamage), merges damage into the next frame's invalid
// set, and enumerates the invalid set as coalesced pixel rects (rows joined
// horizontally, then vertically when cleanly stacked). TileMap.Draw and
// DibBitmap.BltTiles consume it. The C++ enumerators keep their cursor in STATIC
// locals (single-threaded sequencing); here they are instance fields — same
// sequencing, no cross-instance hazard.

import type { CoordMap } from './coords.ts';
import { kpcMax } from './coords.ts';

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}
export interface TRectSmall {
  txLeft: number;
  tyTop: number;
  txRight: number;
  tyBottom: number;
}
export interface MapInfo {
  // ht.h:2507
  cxLeftTile: number;
  cyTopTile: number;
  cxRightTile: number;
  cyBottomTile: number;
  ctxInside: number;
  ctyInside: number;
}

const intersect = (a: Rect, b: Rect): Rect => ({
  left: Math.max(a.left, b.left),
  top: Math.max(a.top, b.top),
  right: Math.min(a.right, b.right),
  bottom: Math.min(a.bottom, b.bottom),
});

const isEmptyRc = (rc: Rect): boolean => rc.left >= rc.right || rc.top >= rc.bottom;

// misc.cpp:180-200 Rect::Intersect — SetEmpty (all zeros) on no overlap
const cppIntersect = (prcSrc1: Rect, prcSrc2: Rect): Rect => {
  const left = Math.max(prcSrc1.left, prcSrc2.left);
  const right = Math.min(prcSrc1.right, prcSrc2.right);
  if (left < right) {
    const top = Math.max(prcSrc1.top, prcSrc2.top);
    const bottom = Math.min(prcSrc1.bottom, prcSrc2.bottom);
    if (top < bottom) return { left, top, right, bottom };
  }
  return { left: 0, top: 0, right: 0, bottom: 0 };
};

// misc.cpp:220-259 Rect::Subtract — prcSrc1 - prcSrc2 (edge-spanning only)
const cppSubtract = (prcSrc1: Rect, prcSrc2: Rect): Rect => {
  let xLeft = prcSrc1.left;
  let yTop = prcSrc1.top;
  let xRight = prcSrc1.right;
  let yBottom = prcSrc1.bottom;

  // Vert edges
  if (prcSrc2.top <= prcSrc1.top && prcSrc2.bottom >= prcSrc1.bottom) {
    // left edge
    if (prcSrc2.left <= prcSrc1.left && prcSrc2.right > prcSrc1.left) xLeft = prcSrc2.right;
    // right edge
    if (prcSrc2.left < prcSrc1.right && prcSrc2.right >= prcSrc1.right) xRight = prcSrc2.left;
  }

  // Horz edges
  if (prcSrc2.left <= prcSrc1.left && prcSrc2.right >= prcSrc1.right) {
    // top edge
    if (prcSrc2.top <= prcSrc1.top && prcSrc2.bottom > prcSrc1.top) yTop = prcSrc2.bottom;
    // bottom edge
    if (prcSrc2.top < prcSrc1.bottom && prcSrc2.bottom >= prcSrc1.bottom) yBottom = prcSrc2.top;
  }

  return { left: xLeft, top: yTop, right: xRight, bottom: yBottom };
};

export class UpdateMap {
  m_ctx = 0;
  m_cty = 0;
  private m_afInvalid: Uint8Array = new Uint8Array(0);
  private m_afInvalidDamage: Uint8Array = new Uint8Array(0);
  private m_fInvalid = false;
  private m_fInvalidDamage = false;
  private m_fMergeDamage = false;
  private m_txOrigin = 0;
  private m_tyOrigin = 0;
  private m_xOriginView = 0;
  private m_yOriginView = 0;
  private m_xMapOffset = 0;
  private m_yMapOffset = 0;
  private m_rcDib: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
  private m_mnfo: MapInfo = {
    cxLeftTile: 0,
    cyTopTile: 0,
    cxRightTile: 0,
    cyBottomTile: 0,
    ctxInside: 0,
    ctyInside: 0,
  };
  private coords: CoordMap;
  /** SetMapOffset clamps the playfield to the level's map size (the C++ reads
   *  gsim.GetLevel()->GetTileMap(); injected here). null = no level yet. */
  mapSizeProvider: (() => { cx: number; cy: number }) | null = null;

  // enumerator cursors (C++ statics in EnumUpdateRects/EnumRowRects)
  private enumLast: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
  private enumDone = false;
  private tyEnumNext = 0;
  private txEnumNext = 0;
  private txLeftBounds = 0;
  private tyTopBounds = 0;
  private txRightBounds = 0;
  private tyBottomBounds = 0;

  constructor(coords: CoordMap) {
    this.coords = coords;
  }

  // game/updatemap.cpp:26-43
  Init(psiz: { cx: number; cy: number }): boolean {
    this.m_rcDib = { left: 0, top: 0, right: psiz.cx, bottom: psiz.cy };
    const t = this.coords.gcxTile;
    this.m_ctx = Math.floor((psiz.cx + t - 1) / t) + 1;
    this.m_cty = Math.floor((psiz.cy + this.coords.gcyTile - 1) / this.coords.gcyTile) + 1;
    const cb = this.m_ctx * this.m_cty;
    this.m_afInvalid = new Uint8Array(cb);
    this.m_afInvalidDamage = new Uint8Array(cb);
    this.SetMapOffset(0, 0, false);
    return true;
  }

  // game/updatemap.cpp:45-50
  Reset(): void {
    this.SetMapOffset(0, 0, true);
    this.m_txOrigin = 0;
    this.m_tyOrigin = 0;
  }

  IsInvalid(): boolean {
    return this.m_fInvalid;
  }
  GetInvalidMap(): Uint8Array {
    return this.m_afInvalid;
  } // ht.h inline
  GetMapSize(): { cx: number; cy: number } {
    return { cx: this.m_ctx, cy: this.m_cty };
  }
  GetMapInfo(): MapInfo {
    return this.m_mnfo;
  }

  // game/updatemap.cpp:57-98 — pairwise vertical coalescing over row rects
  EnumUpdateRects(fFirst: boolean, prcBounds: Rect | null): Rect | null {
    if (fFirst) {
      const first = this.EnumRowRects(true, prcBounds);
      if (first === null) return null;
      this.enumLast = first;
      this.enumDone = false;
    } else if (this.enumDone) {
      return null;
    }
    for (;;) {
      const rcT = this.EnumRowRects(false, prcBounds);
      if (rcT === null) {
        this.enumDone = true;
        return { ...this.enumLast };
      }
      if (
        rcT.left === this.enumLast.left &&
        rcT.right === this.enumLast.right &&
        rcT.top === this.enumLast.bottom
      ) {
        this.enumLast.bottom = rcT.bottom;
        continue;
      }
      const out = { ...this.enumLast };
      this.enumLast = rcT;
      return out;
    }
  }

  // game/updatemap.cpp:100-189 — coalesce runs of invalid tiles into row rects
  private EnumRowRects(fFirst: boolean, prcBounds: Rect | null): Rect | null {
    const t = this.coords.gcxTile;
    const ty = this.coords.gcyTile;
    if (fFirst) {
      if (prcBounds === null) {
        this.txLeftBounds = 0;
        this.tyTopBounds = 0;
        this.txRightBounds = this.m_ctx;
        this.tyBottomBounds = this.m_cty;
      } else {
        this.txLeftBounds = Math.max(0, this.coords.TcFromPc(prcBounds.left + this.m_xMapOffset));
        this.tyTopBounds = Math.max(0, this.coords.TcFromPc(prcBounds.top + this.m_yMapOffset));
        this.txRightBounds = Math.min(
          this.m_ctx,
          this.coords.TcFromPc(prcBounds.right + this.m_xMapOffset + t - 1),
        );
        this.tyBottomBounds = Math.min(
          this.m_cty,
          this.coords.TcFromPc(prcBounds.bottom + this.m_yMapOffset + ty - 1),
        );
      }
      this.txEnumNext = this.txLeftBounds;
      this.tyEnumNext = this.tyTopBounds;
    }
    let txT = this.txEnumNext;
    let txStart = -1;
    let txEnd = -1;
    for (let tyT = this.tyEnumNext; tyT < this.tyBottomBounds; tyT++) {
      txStart = -1;
      txEnd = -1;
      let emit = false;
      for (; txT < this.txRightBounds; txT++) {
        if (this.m_afInvalid[tyT * this.m_ctx + txT]) {
          if (txStart === -1) txStart = txT;
          txEnd = txT + 1;
        } else if (txStart !== -1) {
          this.txEnumNext = txT + 1;
          this.tyEnumNext = tyT;
          emit = true;
          break;
        }
      }
      if (!emit) {
        this.txEnumNext = this.txLeftBounds;
        this.tyEnumNext = tyT + 1;
        txT = this.txLeftBounds;
        if (txStart === -1) continue;
      }
      const prc: Rect = {
        left: txStart * t - this.m_xMapOffset,
        top: tyT * ty - this.m_yMapOffset,
        right: 0,
        bottom: 0,
      };
      prc.right = prc.left + (txEnd - txStart) * t;
      prc.bottom = prc.top + ty;
      return intersect(prc, prcBounds ?? this.m_rcDib);
    }
    return null;
  }

  // game/updatemap.cpp:193-210
  InvalidateTileRect(ptrc: TRectSmall): void {
    for (let tyT = ptrc.tyTop; tyT < ptrc.tyBottom; tyT++) {
      for (let txT = ptrc.txLeft; txT < ptrc.txRight; txT++) {
        const i = tyT * this.m_ctx + txT;
        if (!this.m_afInvalid[i]) {
          this.m_fInvalid = true;
          this.m_afInvalid[i] = 1;
        }
      }
    }
  }

  // game/updatemap.cpp:212-228
  IsTileRectInvalid(ptrc: TRectSmall): boolean {
    for (let tyT = ptrc.tyTop; tyT < ptrc.tyBottom; tyT++) {
      for (let txT = ptrc.txLeft; txT < ptrc.txRight; txT++) {
        if (this.m_afInvalid[tyT * this.m_ctx + txT]) return true;
      }
    }
    return false;
  }

  // game/updatemap.cpp:230-301 — returns [touchingInvalid, newInvalid]
  IsTileRectInvalidAndTrackDamage(ptrc: TRectSmall, fNewInvalid: { v: boolean }): boolean {
    let fAnyInvalid = false;
    outer: for (let tyT = ptrc.tyTop; tyT < ptrc.tyBottom; tyT++) {
      for (let txT = ptrc.txLeft; txT < ptrc.txRight; txT++) {
        if (this.m_afInvalid[tyT * this.m_ctx + txT]) {
          fAnyInvalid = true;
          break outer;
        }
      }
    }
    if (!fAnyInvalid) return false;

    if (this.m_fMergeDamage) {
      let fNew = false;
      for (let tyT = ptrc.tyTop; tyT < ptrc.tyBottom; tyT++) {
        for (let txT = ptrc.txLeft; txT < ptrc.txRight; txT++) {
          const i = tyT * this.m_ctx + txT;
          if (!this.m_afInvalid[i]) {
            this.m_afInvalid[i] = 1;
            fNew = true;
          }
        }
      }
      fNewInvalid.v ||= fNew;
    } else {
      for (let tyT = ptrc.tyTop; tyT < ptrc.tyBottom; tyT++) {
        for (let txT = ptrc.txLeft; txT < ptrc.txRight; txT++) {
          this.m_afInvalidDamage[tyT * this.m_ctx + txT] = 1;
          this.m_fInvalidDamage = true;
        }
      }
    }
    return true;
  }

  // ht.h:2544-2549
  IsRectInvalid(prc: Rect): boolean {
    return this.IsTileRectInvalid(this.CalcTileRect(prc));
  }

  // ht.h:2551-2557
  IsRectInvalidAndTrackDamage(prc: Rect): boolean {
    const fNewInvalid = { v: false };
    return this.IsTileRectInvalidAndTrackDamage(this.CalcTileRect(prc), fNewInvalid);
  }

  // game/updatemap.cpp:303-325
  IsMapTileRectInvalidAndTrackDamage(ptrc: TRectSmall, fNewInvalid: { v: boolean }): boolean {
    const trc: TRectSmall = {
      txLeft: Math.max(0, ptrc.txLeft - this.m_txOrigin),
      tyTop: Math.max(0, ptrc.tyTop - this.m_tyOrigin),
      txRight: Math.min(this.m_ctx, ptrc.txRight - this.m_txOrigin),
      tyBottom: Math.min(this.m_cty, ptrc.tyBottom - this.m_tyOrigin),
    };
    return this.IsTileRectInvalidAndTrackDamage(trc, fNewInvalid);
  }

  // game/updatemap.cpp:327-331
  Validate(): void {
    this.m_afInvalid.fill(0);
    this.m_fInvalid = false;
  }

  // game/updatemap.cpp:333-370
  CalcTileRect(prc: Rect): TRectSmall {
    const t = this.coords.gcxTile;
    const ty = this.coords.gcyTile;
    let x = prc.left + this.m_xMapOffset;
    if (x >= kpcMax) x = kpcMax - 1;
    if (x < 0) x = 0;
    let y = prc.top + this.m_yMapOffset;
    if (y >= kpcMax) y = kpcMax - 1;
    if (y < 0) y = 0;
    let xT = x + (prc.right - prc.left) + (t - 1);
    if (xT >= kpcMax) xT = kpcMax - 1;
    let yT = y + (prc.bottom - prc.top) + (ty - 1);
    if (yT >= kpcMax) yT = kpcMax - 1;
    return {
      txLeft: this.coords.TcFromPc(x),
      tyTop: this.coords.TcFromPc(y),
      txRight: Math.min(this.m_ctx, this.coords.TcFromPc(xT)),
      tyBottom: Math.min(this.m_cty, this.coords.TcFromPc(yT)),
    };
  }

  // game/updatemap.cpp:372-383
  InvalidateRect(prc: Rect | null = null): void {
    const t = this.coords.gcxTile;
    const rc = prc ?? {
      left: 0,
      top: 0,
      right: this.m_ctx * t,
      bottom: this.m_cty * this.coords.gcyTile,
    };
    this.InvalidateTileRect(this.CalcTileRect(rc));
  }

  // game/updatemap.cpp:385-421
  InvalidateMapTileRect(ptrc: TRectSmall): void {
    const trc: TRectSmall = {
      txLeft: ptrc.txLeft - this.m_txOrigin,
      tyTop: ptrc.tyTop - this.m_tyOrigin,
      txRight: ptrc.txRight - this.m_txOrigin,
      tyBottom: ptrc.tyBottom - this.m_tyOrigin,
    };
    if (trc.txLeft < 0) {
      if (trc.txRight <= 0) return;
      trc.txLeft = 0;
    }
    if (trc.tyTop < 0) {
      if (trc.tyBottom <= 0) return;
      trc.tyTop = 0;
    }
    if (trc.txRight > this.m_ctx) {
      if (trc.txLeft >= this.m_ctx) return;
      trc.txRight = this.m_ctx;
    }
    if (trc.tyBottom > this.m_cty) {
      if (trc.tyTop >= this.m_cty) return;
      trc.tyBottom = this.m_cty;
    }
    this.InvalidateTileRect(trc);
  }

  // game/updatemap.cpp:423-442
  InvalidateTile(txMap: number, tyMap: number): void {
    const tx = txMap - this.m_txOrigin;
    const ty = tyMap - this.m_tyOrigin;
    if (tx < 0 || tx >= this.m_ctx || ty < 0 || ty >= this.m_cty) return;
    const i = ty * this.m_ctx + tx;
    if (this.m_afInvalid[i]) return;
    this.m_afInvalid[i] = 1;
    this.m_fInvalid = true;
  }

  // game/updatemap.cpp:500-653 — scroll the invalid + damage tile maps by
  // (dx, dy) pixels: recompute the sub-tile offsets, shift whole tiles through
  // a temp buffer (newly exposed tiles become invalid), then invalidate the
  // entering edges.
  //
  // The C++ QUIRK — the fill(1) of newly exposed tiles did NOT set m_fInvalid
  // (only InvalidateTileRect's 0→1 transitions did) — is deliberately FIXED here.
  // On a scroll of a full screen or more the shifted-in window vanishes entirely
  // (the txDst/tyDst guard below), leaving EVERY tile 1, so the edge
  // InvalidateRect finds no 0→1 transition and m_fInvalid stays false: the next
  // Paint early-outs on IsInvalid() and DrawFrame's unconditional Validate()
  // then WIPES the all-invalid record — a minimap jump landed on a frozen,
  // never-repainted frame (stale until unrelated damage healed its own tiles).
  // The C++ never hit this only because some canvas form (the minimap control /
  // the SDL full-repaint FrameStart) invalidated fresh tiles every frame.
  Scroll(dx: number, dy: number): boolean {
    // If no scroll, nothing to do

    if (dx === 0 && dy === 0) return false;
    const c = this.coords;

    // Calc new sub-tile map offsets

    let xMapOffsetNew: number;
    if (dx <= 0) {
      xMapOffsetNew = c.PcFracFromUpc(this.m_xMapOffset - dx);
    } else {
      xMapOffsetNew = c.PcFracFromUpc(
        c.gcxTile - c.PcFracFromUpc(c.gcxTile - this.m_xMapOffset + dx),
      );
    }
    let yMapOffsetNew: number;
    if (dy <= 0) {
      yMapOffsetNew = c.PcFracFromUpc(this.m_yMapOffset - dy);
    } else {
      yMapOffsetNew = c.PcFracFromUpc(
        c.gcyTile - c.PcFracFromUpc(c.gcyTile - this.m_yMapOffset + dy),
      );
    }

    // Figure out the number of whole tiles to scroll

    let dtx: number;
    if (dx <= 0) {
      dtx = -c.TcFromPc(this.m_xMapOffset - dx);
    } else {
      dtx = c.TcFromPc(c.gcxTile - 1 - this.m_xMapOffset + dx);
    }
    let dty: number;
    if (dy <= 0) {
      dty = -c.TcFromPc(this.m_yMapOffset - dy);
    } else {
      dty = c.TcFromPc(c.gcyTile - 1 - this.m_yMapOffset + dy);
    }

    // Adjust tile origin

    this.m_txOrigin -= dtx;
    this.m_tyOrigin -= dty;

    // Scroll the map if needed. If we don't actually need to scroll the map,
    // we still need to cause invalidation along the edges

    if (dtx !== 0 || dty !== 0) {
      // Need to scroll the map. Figure out the scrolling rect (clipped)

      const rcSrc: Rect = {
        left: -dtx,
        top: -dty,
        right: this.m_ctx - dtx,
        bottom: this.m_cty - dty,
      };
      if (rcSrc.left < 0) rcSrc.left = 0;
      if (rcSrc.top < 0) rcSrc.top = 0;
      if (rcSrc.right > this.m_ctx) rcSrc.right = this.m_ctx;
      if (rcSrc.bottom > this.m_cty) rcSrc.bottom = this.m_cty;

      // Figure out dst

      let txDst = dtx;
      if (txDst < 0) txDst = 0;
      let tyDst = dty;
      if (tyDst < 0) tyDst = 0;

      // Don't need to worry about copy direction since we're using a temp buffer

      const ctx = rcSrc.right - rcSrc.left;
      const cty = rcSrc.bottom - rcSrc.top;

      // Scroll the invalid map. The fill(1) marks the newly exposed tiles
      // invalid, so the flag MUST rise with it (see the header note) — a
      // whole-tile scroll always exposes at least one row/column.

      const pfT = this.m_afInvalid.slice();
      this.m_afInvalid.fill(1);
      this.m_fInvalid = true;
      if (txDst < this.m_ctx && tyDst < this.m_cty) {
        for (let ty = 0; ty < cty; ty++) {
          for (let tx = 0; tx < ctx; tx++) {
            this.m_afInvalid[(tyDst + ty) * this.m_ctx + txDst + tx] =
              pfT[(rcSrc.top + ty) * this.m_ctx + rcSrc.left + tx];
          }
        }
      }

      // Scroll the damage map

      pfT.set(this.m_afInvalidDamage);
      this.m_afInvalidDamage.fill(0);
      if (txDst < this.m_ctx && tyDst < this.m_cty) {
        for (let ty = 0; ty < cty; ty++) {
          for (let tx = 0; tx < ctx; tx++) {
            this.m_afInvalidDamage[(tyDst + ty) * this.m_ctx + txDst + tx] =
              pfT[(rcSrc.top + ty) * this.m_ctx + rcSrc.left + tx];
          }
        }
      }
    }

    // Update the new map offsets before edge invalidation

    this.SetMapOffset(xMapOffsetNew, yMapOffsetNew, false);

    // Invalidate edges

    if (dx !== 0) {
      let rcNew: Rect = { ...this.m_rcDib };
      rcNew.left += dx;
      rcNew.right += dx;
      rcNew = cppIntersect(rcNew, this.m_rcDib);
      const rcSub = cppSubtract(this.m_rcDib, rcNew);
      if (!isEmptyRc(rcSub)) this.InvalidateRect(rcSub);
    }
    if (dy !== 0) {
      let rcNew: Rect = { ...this.m_rcDib };
      rcNew.top += dy;
      rcNew.bottom += dy;
      rcNew = cppIntersect(rcNew, this.m_rcDib);
      const rcSub = cppSubtract(this.m_rcDib, rcNew);
      if (!isEmptyRc(rcSub)) this.InvalidateRect(rcSub);
    }

    // Return true to cause bits to be scrolled

    return true;
  }

  // game/updatemap.cpp:444-459
  StartMergeDamagedInvalid(): void {
    for (let i = 0; i < this.m_afInvalid.length; i++) {
      this.m_afInvalid[i] |= this.m_afInvalidDamage[i];
    }
    // merged damage tiles are invalid tiles — the paint gate (IsInvalid) must
    // see them or the post-paint Validate() silently discards the damage
    if (this.m_fInvalidDamage) this.m_fInvalid = true;
    this.m_fMergeDamage = true;
  }

  EndMergeDamagedInvalid(): void {
    this.m_afInvalidDamage.fill(0);
    this.m_fInvalidDamage = false;
    this.m_fMergeDamage = false;
  }

  // game/updatemap.cpp:461-465
  SetViewOrigin(xOrigin: number, yOrigin: number): void {
    this.m_xOriginView = xOrigin;
    this.m_yOriginView = yOrigin;
  }

  SetTileOrigin(txOrigin: number, tyOrigin: number): void {
    // ht.h inline
    this.m_txOrigin = txOrigin;
    this.m_tyOrigin = tyOrigin;
  }

  // game/updatemap.cpp:467-493
  SetMapOffset(xMapOffset: number, yMapOffset: number, fInvalidate: boolean): void {
    this.m_xMapOffset = xMapOffset;
    this.m_yMapOffset = yMapOffset;
    let cxDib = this.m_rcDib.right - this.m_rcDib.left;
    let cyDib = this.m_rcDib.bottom - this.m_rcDib.top;
    if (this.mapSizeProvider !== null) {
      const sizTMap = this.mapSizeProvider();
      if (sizTMap.cx < cxDib) cxDib = sizTMap.cx;
      if (sizTMap.cy < cyDib) cyDib = sizTMap.cy;
    }
    const c = this.coords;
    this.m_mnfo.cxLeftTile = c.PcFracFromUpc(c.gcxTile - this.m_xMapOffset);
    if (cyDib < c.gcyTile - this.m_yMapOffset) {
      this.m_mnfo.cyTopTile = cyDib;
    } else {
      this.m_mnfo.cyTopTile = c.PcFracFromUpc(c.gcyTile - this.m_yMapOffset);
    }
    this.m_mnfo.cxRightTile = c.PcFracFromUpc(cxDib - this.m_mnfo.cxLeftTile);
    this.m_mnfo.cyBottomTile = c.PcFracFromUpc(cyDib - this.m_mnfo.cyTopTile);
    this.m_mnfo.ctxInside = c.TcFromPc(cxDib - this.m_mnfo.cxLeftTile - this.m_mnfo.cxRightTile);
    this.m_mnfo.ctyInside = c.TcFromPc(cyDib - this.m_mnfo.cyTopTile - this.m_mnfo.cyBottomTile);
    if (fInvalidate) this.InvalidateRect();
  }
}
